import { Box, CircularProgress, Container, LinearProgress } from '@mui/material';
import { useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
//----------------------------------------------------------------------
import { CalendarUI } from '../../../utils/Events/Calendar';
import EventPageLayout from '../../../utils/Events/EventPageLayout';
import API from '../../../api';

const computeInitialRange = (anchor) => {
  const date = anchor ? dayjs(anchor) : dayjs();
  return {
    from: date.startOf('month').subtract(7, 'day').format('YYYY-MM-DD'),
    to: date.endOf('month').add(7, 'day').format('YYYY-MM-DD'),
  };
};

const cacheKey = (from, to) => `${from}_${to}`;

const mapApiItem = (type) => (item) => ({
  start: new Date(item.start_date),
  end: new Date(item.end_date),
  allDay: true,
  type,
  ...item,
});

export default function Events() {
  const location = useLocation();
  const requestDay = location.state ? location.state.requestDay : null;

  const [hasAuth, setHasAuth] = useState(false);

  const [celebrations, setCelebrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [range, setRange] = useState(() => computeInitialRange(requestDay));

  const eventsCacheRef = useRef(new Map());
  const celebrationsCacheRef = useRef(new Map());

  const fetchEvents = useCallback((from, to) => {
    const key = cacheKey(from, to);
    if (eventsCacheRef.current.has(key)) {
      setEvents(eventsCacheRef.current.get(key));
      return Promise.resolve();
    }
    return API.get(`/events?from=${from}&to=${to}`).then((response) => {
      const mapped = response.data.map(mapApiItem('event'));
      eventsCacheRef.current.set(key, mapped);
      setEvents(mapped);
    });
  }, []);

  const fetchCelebrations = useCallback((from, to) => {
    const key = cacheKey(from, to);
    if (celebrationsCacheRef.current.has(key)) {
      setCelebrations(celebrationsCacheRef.current.get(key));
      return Promise.resolve();
    }
    return API.get(`/celebrations?from=${from}&to=${to}`).then((response) => {
      const mapped = response.data.map(mapApiItem('celebration'));
      celebrationsCacheRef.current.set(key, mapped);
      setCelebrations(mapped);
    });
  }, []);

  const refetchCurrentRange = useCallback(() => {
    eventsCacheRef.current.delete(cacheKey(range.from, range.to));
    celebrationsCacheRef.current.delete(cacheKey(range.from, range.to));
    setIsFetching(true);
    Promise.all([fetchEvents(range.from, range.to), fetchCelebrations(range.from, range.to)]).finally(() =>
      setIsFetching(false)
    );
  }, [range, fetchEvents, fetchCelebrations]);

  useEffect(() => {
    setIsFetching(true);
    Promise.all([fetchEvents(range.from, range.to), fetchCelebrations(range.from, range.to)]).finally(() => {
      setIsFetching(false);
      setIsInitialLoaded(true);
    });
  }, [range, fetchEvents, fetchCelebrations]);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('userData'));
    if (data?.can_manage_celebrations) setHasAuth(true);
  }, []);

  const handleRangeChange = useCallback((nextRange) => {
    const dates = Array.isArray(nextRange) ? nextRange : [nextRange.start, nextRange.end];
    const from = dayjs(dates[0]).format('YYYY-MM-DD');
    const to = dayjs(dates[dates.length - 1]).format('YYYY-MM-DD');
    setRange((current) => (current.from === from && current.to === to ? current : { from, to }));
  }, []);

  // general
  const [currentProfile, setCurrentProfile] = useState('');

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      {isInitialLoaded ? (
        <EventPageLayout
          isAdmin
          hasAuth={hasAuth}
          celebrations={celebrations}
          events={events}
          getCelebrations={refetchCurrentRange}
          getEvents={refetchCurrentRange}
        >
          <Box sx={{ position: 'relative' }}>
            {isFetching && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, borderRadius: 1 }} />
            )}
            <CalendarUI
              isAdmin
              hasAuth={hasAuth}
              celebrations={celebrations}
              events={events}
              requestDay={requestDay}
              onRangeChange={handleRangeChange}
            />
          </Box>
        </EventPageLayout>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
